import React, { useEffect, useMemo, useState } from "react";
import api from "config/axiosConfig";
import AuthService from "services/authService";
import { getAccountSettingsUiStrings, resolveAccountSettingsLocale } from "i18n/accountSettingsUi";
import ModalOrDrawer from "ui/ModalOrDrawer";
import InputField from "ui/InputField";
import { normalizeApiError } from "util/normalizeApiError";
import { apiRoutes } from "util/apiRoutes";

interface UserSettingsAccountGoogleDisconnectProps {
    hasPassword: boolean;
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => Promise<void>;
}

const UserSettingsAccountGoogleDisconnect: React.FC<UserSettingsAccountGoogleDisconnectProps> = ({
    hasPassword,
    isOpen,
    onClose,
    onSuccess,
}) => {
    const ui = getAccountSettingsUiStrings(resolveAccountSettingsLocale());
    const [currentPassword, setCurrentPassword] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSentState, setShowSentState] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setCurrentPassword("");
            setError(null);
            setIsSubmitting(false);
            setShowSentState(false);
        }
    }, [isOpen]);

    const footer = useMemo(() => {
        if (!hasPassword) {
            if (showSentState) {
                return (
                    <button
                        type="button"
                        className="btn-primary w-full sm:w-fit py-2 px-4"
                        onClick={onClose}
                    >
                        {ui.modalDone}
                    </button>
                );
            }

            return (
                <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
                    <button
                        type="button"
                        className="btn-secondary w-full sm:w-fit py-2 px-4"
                        onClick={onClose}
                    >
                        {ui.modalCancel}
                    </button>
                    <button
                        type="button"
                        className="btn-primary w-full sm:w-fit py-2 px-4"
                        disabled={isSubmitting}
                        onClick={async () => {
                            setIsSubmitting(true);
                            setError(null);
                            try {
                                await api.post(apiRoutes.auth.googleDisconnectStart);
                                setShowSentState(true);
                            } catch (requestError: unknown) {
                                setError(normalizeApiError(requestError, ui.googleDisconnectError));
                            } finally {
                                setIsSubmitting(false);
                            }
                        }}
                    >
                        {ui.googleDisconnectSetupButton}
                    </button>
                </div>
            );
        }

        return (
            <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
                <button
                    type="button"
                    className="btn-secondary w-full sm:w-fit py-2 px-4"
                    onClick={onClose}
                >
                    {ui.modalCancel}
                </button>
                <button
                    type="button"
                    className="btn-primary w-full sm:w-fit py-2 px-4"
                    disabled={isSubmitting || currentPassword.trim().length === 0}
                    onClick={async () => {
                        if (!currentPassword.trim()) {
                            setError(ui.passwordRequiredError);
                            return;
                        }
                        setIsSubmitting(true);
                        setError(null);
                        try {
                            const response = await api.post(apiRoutes.auth.googleDisconnectConfirm, {
                                currentPassword,
                            });
                            if (typeof response.data?.accessToken === "string" && response.data.accessToken.length > 0) {
                                AuthService.setAccessToken(response.data.accessToken);
                            }
                            await onSuccess();
                            onClose();
                        } catch (requestError: unknown) {
                            setError(normalizeApiError(requestError, ui.googleDisconnectError));
                        } finally {
                            setIsSubmitting(false);
                        }
                    }}
                >
                    {ui.googleDisconnectSaveButton}
                </button>
            </div>
        );
    }, [currentPassword, hasPassword, isSubmitting, onClose, onSuccess, showSentState, ui]);

    return (
        <ModalOrDrawer
            isOpen={isOpen}
            onClose={onClose}
            title={
                !hasPassword && showSentState
                    ? ui.googleDisconnectSetupSentTitle
                    : hasPassword
                        ? ui.googleDisconnectConfirmTitle
                        : ui.googleDisconnectSetupTitle
            }
            footer={footer}
            stickyFooter
            className="text-center"
        >
            <div className="flex flex-col gap-4 py-2">
                {!hasPassword && showSentState ? (
                    <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        {ui.googleDisconnectSetupSentDescription}
                    </p>
                ) : hasPassword ? (
                    <>
                        <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {ui.googleDisconnectConfirmDescription}
                        </p>
                        <InputField
                            type="password"
                            id="googleDisconnectPassword"
                            name="googleDisconnectPassword"
                            labelText={ui.googleDisconnectPasswordLabel}
                            autoComplete="current-password"
                            fieldRequired
                            value={currentPassword}
                            onChange={(event) => setCurrentPassword(event.target.value)}
                            error={error ?? undefined}
                            spellCheck={false}
                        />
                    </>
                ) : (
                    <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        {ui.googleDisconnectSetupDescription}
                    </p>
                )}
                {!hasPassword && error ? (
                    <p className="text-sm text-warning-red dark:text-warning-red-dark">{error}</p>
                ) : null}
            </div>
        </ModalOrDrawer>
    );
};

export default UserSettingsAccountGoogleDisconnect;
